<template>
    <div class="order-info-item">
        <div class="customer-section">
            <h3 class="section-title">Kunde</h3>
            <div class="customer-details">
                <div class="detail-row">
                    <span class="detail-label">Name:</span>
                    <span class="detail-value">{{ customerName || 'Nicht verfügbar' }}</span>
                </div>
                <div class="detail-row">
                    <span class="detail-label">Adresse:</span>
                    <span class="detail-value">{{ customerAddress || 'Nicht verfügbar' }}</span>
                </div>
                <div class="detail-row">
                    <span class="detail-label">Land:</span>
                    <span class="detail-value country">
                        <span :class="'fi fi-' + getCountryCode(country)" class="country-flag-icon"></span>
                        <span class="country-name">{{ country || 'Unbekannt' }}</span>
                    </span>
                </div>
            </div>
        </div>
        
        <div class="order-section">
            <h3 class="section-title">Bestellung</h3>
            <div class="order-details">
                <div 
                    class="detail-row"
                    v-if="user?.role === 'admin'"
                >
                    <span class="detail-label">Order ID:</span>
                    <div 
                        class="order-id-container" 
                        @mouseenter="showRedirectIcon = true" 
                        @mouseleave="showRedirectIcon = false"
                        @click="redirectToOrder(orderId)"
                    >
                        <span class="detail-value order-id">{{ orderId || 'Nicht verfügbar' }}</span>
                        <v-icon
                            v-show="showRedirectIcon"
                            color="#1976d2"
                            x-small
                            class="redirect-button"
                        >mdi-open-in-new</v-icon>
                    </div>
                </div>
                <div v-else class="detail-row">
                    <span class="detail-label">Order ID:</span>
                    <span class="detail-value order-id">{{ orderId || 'Nicht verfügbar' }}</span>
                </div>
                <div class="detail-row">
                    <span class="detail-label">Bestelleingang:</span>
                    <span class="detail-value order-id">{{ formatDate(createDate) || 'Nicht verfügbar' }}</span>
                </div>
            </div>
        </div>
        <hr>
        <div class="action-buttons">
            <v-btn
                color="error"
                @click="openProblemModal"
                block
            >
                <v-icon>mdi-pencil</v-icon>
                <span>Problem melden</span>
            </v-btn>
            <v-btn
                color="orange"
                @click="openResetDialog"
                block
                dark
            >
                <v-icon>mdi-refresh-circle</v-icon>
                <span>Artikel zurücksetzen</span>
            </v-btn>
        </div>
    </div>
</template>

<script>
export default {
    name: 'OrderInfoItem',
    props: {
        customerName: {
            type: String,
            default: null
        },
        customerAddress: {
            type: String,
            default: null
        },
        createDate: {
            type: String,
            default: null
        },
        orderId: {
            type: String,
            default: null
        },
        currentPackageWeight: {
            type: Number,
            default: 0
        },
        country: {
            type: String,
            default: null
        },
        openProblemModal: {
            type: Function,
            default: null
        },
        openResetDialog: {
            type: Function,
            default: null
        },
        user: {
            type: Object,
            default: null
        }
    },
    data() {
        return {
            showRedirectIcon: false
        }
    },
    methods: {
        getCountryCode(countryName) {
            // Map German country names to ISO 3166-1-alpha-2 codes (lowercase for flag-icons)
            const countryCodes = {
                'Deutschland': 'de',
                'Österreich': 'at', 
                'Schweiz': 'ch',
                'USA': 'us',
                'Vereinigtes Königreich': 'gb',
                'Frankreich': 'fr',
                'Italien': 'it',
                'Spanien': 'es',
                'Niederlande': 'nl',
                'Belgien': 'be',
                'Dänemark': 'dk',
                'Schweden': 'se',
                'Norwegen': 'no',
                'Finnland': 'fi',
                'Polen': 'pl',
                'Tschechien': 'cz',
                'Ungarn': 'hu',
                'Slowakei': 'sk',
                'Slowenien': 'si',
                'Kroatien': 'hr',
                'Rumänien': 'ro',
                'Bulgarien': 'bg',
                'Griechenland': 'gr',
                'Portugal': 'pt',
                'Irland': 'ie',
                'Luxemburg': 'lu',
                'Kanada': 'ca',
                'Australien': 'au',
                'Japan': 'jp',
                'China': 'cn',
                'Indien': 'in',
                'Brasilien': 'br',
                'Mexiko': 'mx',
                'Argentinien': 'ar',
                'Chile': 'cl',
                'Kolumbien': 'co',
                'Peru': 'pe',
                'Venezuela': 've',
                'Uruguay': 'uy',
                'Paraguay': 'py',
                'Bolivien': 'bo',
                'Ecuador': 'ec'
            };
            return countryCodes[countryName] || 'un'; // 'un' for United Nations flag as fallback
        },
        formatDate(date) {
            return new Date(date).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
        },
        redirectToOrder(orderId) {
            if (orderId) {
                // Open support page with order ID as query parameter
                const url = `${window.location.origin}/support?search=${orderId}`;
                window.open(url, '_blank');
            }
        }
    }
}
</script>

<style scoped>
.order-info-item {
    display: flex;
    flex-direction: column;
    gap: 16px;
    /* No padding, margin, background, or border */
}

.customer-section,
.order-section {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.section-title {
    font-size: 16px;
    font-weight: 700;
    color: #333;
    margin: 0;
    text-transform: uppercase;
    letter-spacing: 1px;
    border-bottom: 2px solid #1976d2;
    padding-bottom: 4px;
    display: inline-block;
    width: fit-content;
}

.customer-details,
.order-details {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-left: 8px;
}

.detail-row {
    display: flex;
    align-items: baseline;
    gap: 8px;
}

.order-id-container {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    position: relative;
    transition: all 0.3s ease;
    border-radius: 6px;
    padding: 2px 6px;
    cursor: pointer;
}

.order-id-container:hover {
    background-color: rgba(25, 118, 210, 0.05);
    transform: translateX(2px);
}

.redirect-button {
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    opacity: 0;
    transform: translateX(-8px) scale(0.8);
}

.order-id-container:hover .redirect-button {
    opacity: 1;
    transform: translateX(0) scale(1);
}

.redirect-button:hover {
    transform: translateX(0) scale(1.15);
    background-color: rgba(25, 118, 210, 0.1);
    border-radius: 50%;
}

.detail-label {
    font-size: 12px;
    font-weight: 600;
    color: #666;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    min-width: 60px;
    flex-shrink: 0;
}

.detail-value {
    font-size: 14px;
    font-weight: 500;
    color: #333;
    flex: 1;
    word-wrap: break-word;
}

.detail-value.order-id {
    font-family: 'Monaco', 'Consolas', monospace;
    color: #1976d2;
    font-weight: 600;
}

.detail-value.weight {
    font-family: 'Monaco', 'Consolas', monospace;
    color: #ff9800;
    font-weight: 600;
}

.detail-value.country {
    display: flex;
    align-items: center;
    gap: 8px;
}

.country-flag-icon {
    width: 24px;
    height: 18px;
    border-radius: 2px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
    display: inline-block;
    flex-shrink: 0;
}

.country-name {
    color: #4caf50;
    font-weight: 600;
}

.action-buttons {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

/* Responsive adjustments */
@media (max-width: 768px) {
    .order-info-item {
        gap: 12px;
    }
    
    .section-title {
        font-size: 14px;
    }
    
    .detail-label {
        font-size: 11px;
        min-width: 50px;
    }
    
    .detail-value {
        font-size: 13px;
    }
    
    .country-flag-icon {
        width: 20px;
        height: 15px;
    }
}

@media (max-width: 480px) {
    .order-info-item {
        gap: 10px;
    }
    
    .section-title {
        font-size: 13px;
    }
    
    .detail-row {
        flex-direction: column;
        align-items: flex-start;
        gap: 2px;
    }
    
    .detail-label {
        font-size: 10px;
        min-width: auto;
    }
    
    .detail-value {
        font-size: 12px;
    }
    
    .country-flag-icon {
        width: 18px;
        height: 13px;
    }
}
</style>